import clsx from 'clsx'
import type { MenuItemProps } from './MenuItem'

const variantStyles = {
  primary: {
    item: clsx(
      'hover:bg-brand-neutral-100 focus:bg-brand-neutral-100 data-[highlighted]:bg-brand-neutral-100',
      'data-[selected]:bg-brand-neutral-200',
      'data-[disabled]:hover:bg-transparent data-[disabled]:focus:bg-transparent data-[disabled]:cursor-not-allowed'
    ),
    itemHighlighted: 'bg-brand-neutral-100',
    itemSelected: 'bg-brand-neutral-200',
    icon: clsx(
      'text-brand-neutral-600',
      'group-hover:text-brand-500 group-focus:text-brand-500 group-data-[highlighted]:text-brand-500 group-data-[selected]:text-brand-500',
      'group-data-[disabled]:text-true-gray-300'
    ),
    iconHighlighted: 'text-brand-500',
    iconSelected: 'text-brand-500',
    iconDisabled: 'text-true-gray-300',
    label: clsx(
      'text-brand-neutral-700',
      'group-hover:text-brand-500 group-focus:text-brand-500 group-data-[highlighted]:text-brand-500 group-data-[selected]:text-brand-500',
      'group-data-[disabled]:text-true-gray-300'
    ),
    labelHighlighted: 'text-brand-500',
    labelSelected: 'text-brand-500',
    labelDisabled: 'text-true-gray-300',
    subheading: clsx(
      'text-brand-neutral-600',
      'group-hover:text-brand-neutral-800 group-focus:text-brand-neutral-800 group-data-[highlighted]:text-brand-neutral-800 group-data-[selected]:text-brand-neutral-800',
      'group-data-[disabled]:text-true-gray-300'
    ),
    subheadingHighlighted: 'text-brand-neutral-800',
    subheadingSelected: 'text-brand-neutral-800',
    subheadingDisabled: 'text-true-gray-300',
  },
  neutral: {
    item: clsx(
      'hover:bg-brand-neutral-100 focus:bg-brand-neutral-100 data-[highlighted]:bg-brand-neutral-100',
      'data-[selected]:bg-brand-neutral-200',
      'data-[disabled]:hover:bg-transparent data-[disabled]:focus:bg-transparent data-[disabled]:cursor-not-allowed'
    ),
    itemHighlighted: 'bg-brand-neutral-100',
    itemSelected: 'bg-brand-neutral-200',
    icon: clsx(
      'text-brand-neutral-600',
      'group-hover:text-brand-neutral-800 group-focus:text-brand-neutral-800 group-data-[highlighted]:text-brand-neutral-800 group-data-[selected]:text-brand-neutral-950',
      'group-data-[disabled]:text-true-gray-300'
    ),
    iconHighlighted: 'text-brand-neutral-800',
    iconSelected: 'text-brand-neutral-950',
    iconDisabled: 'text-true-gray-300',
    label: clsx(
      'text-brand-neutral-700',
      'group-hover:text-brand-neutral-800 group-focus:text-brand-neutral-800 group-data-[highlighted]:text-brand-neutral-800 group-data-[selected]:text-brand-neutral-950',
      'group-data-[disabled]:text-true-gray-300'
    ),
    labelHighlighted: 'text-brand-neutral-800',
    labelSelected: 'text-brand-neutral-950',
    labelDisabled: 'text-true-gray-300',
    subheading: clsx(
      'text-brand-neutral-600',
      'group-hover:text-brand-neutral-800 group-focus:text-brand-neutral-800 group-data-[highlighted]:text-brand-neutral-800 group-data-[selected]:text-brand-neutral-800',
      'group-data-[disabled]:text-true-gray-300'
    ),
    subheadingHighlighted: 'text-brand-neutral-800',
    subheadingSelected: 'text-brand-neutral-800',
    subheadingDisabled: 'text-true-gray-300',
  },
  destructive: {
    item: clsx(
      'hover:bg-red-50 focus:bg-red-50 data-[highlighted]:bg-red-50',
      'data-[selected]:bg-red-100',
      'data-[disabled]:hover:bg-transparent data-[disabled]:focus:bg-transparent data-[disabled]:cursor-not-allowed'
    ),
    itemHighlighted: 'bg-red-50',
    itemSelected: 'bg-red-100',
    icon: clsx(
      'text-red-700',
      'group-hover:text-red-800 group-focus:text-red-800 group-data-[highlighted]:text-red-800 group-data-[selected]:text-red-800',
      'group-data-[disabled]:text-true-gray-300'
    ),
    iconHighlighted: 'text-red-800',
    iconSelected: 'text-red-800',
    iconDisabled: 'text-true-gray-300',
    label: clsx(
      'text-red-700',
      'group-hover:text-red-800 group-focus:text-red-800 group-data-[highlighted]:text-red-800 group-data-[selected]:text-red-800',
      'group-data-[disabled]:text-true-gray-300'
    ),
    labelHighlighted: 'text-red-800',
    labelSelected: 'text-red-800',
    labelDisabled: 'text-true-gray-300',
    subheading: clsx(
      'text-red-700',
      'group-hover:text-red-800 group-focus:text-red-800 group-data-[highlighted]:text-red-800 group-data-[selected]:text-red-800',
      'group-data-[disabled]:text-true-gray-300'
    ),
    subheadingHighlighted: 'text-red-800',
    subheadingSelected: 'text-red-800',
    subheadingDisabled: 'text-true-gray-300',
  },
} as const

type MenuItemState = Pick<MenuItemProps, 'disabled' | 'selected' | 'subheading' | 'variant'> & {
  highlighted?: boolean
  className?: string
}

export function getMenuItemClassName({ className, disabled = false, highlighted = false, selected = false, subheading, variant = 'primary' }: MenuItemState) {
  const styles = variantStyles[variant]

  return clsx(
    'group flex cursor-pointer items-center gap-2.5 rounded text-left focus:outline-none w-full',
    subheading ? 'min-h-10 px-2 py-0' : 'p-2 text-sm font-medium tracking-[-0.15px]',
    styles.item,
    highlighted && styles.itemHighlighted,
    selected && styles.itemSelected,
    disabled && 'cursor-not-allowed',
    className
  )
}

type MenuItemContentState = Pick<MenuItemProps, 'disabled' | 'selected' | 'variant'> & {
  highlighted?: boolean
}

export function getMenuItemContentClassNames({ disabled = false, highlighted = false, selected = false, variant = 'primary' }: MenuItemContentState) {
  const styles = variantStyles[variant]

  return {
    iconClassName: clsx(
      'h-4 w-4 shrink-0',
      styles.icon,
      highlighted && styles.iconHighlighted,
      selected && styles.iconSelected,
      disabled && styles.iconDisabled
    ),
    labelClassName: clsx(
      'text-sm font-medium tracking-[-0.15px] leading-5',
      styles.label,
      highlighted && styles.labelHighlighted,
      selected && styles.labelSelected,
      disabled && styles.labelDisabled
    ),
    subheadingClassName: clsx(
      'text-xs leading-4 tracking-normal whitespace-nowrap truncate',
      styles.subheading,
      highlighted && styles.subheadingHighlighted,
      selected && styles.subheadingSelected,
      disabled && styles.subheadingDisabled
    ),
  }
}
